import React, { useContext, useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import { useAds } from 'hooks/use-ads';
import { ProductImpressionData } from '../product-impression.interface';
import { TeApiContext } from '@ecommerce/nest-ssr/common';

interface DasImpressionData {
    id: string;
    name: string;
    price: string;
    brand?: string;
    category: string;
    qty?: number;
}

interface ProductImpressionProps {
    trackedData: ProductImpressionData;
    className?: string;
    children: React.ReactNode;
}

const productsImpressions: ProductImpressionData[] = [];

function mapToDasImpression(products: ProductImpressionData[]): DasImpressionData[] {
    return products.map(product => ({
        name: product.name,
        id: String(product.id),
        price: String(product.price),
        brand: product.producer?.name,
        category: product.category.name,
        qty: 1
    }));
}

function getSitetype(location: string): string {
    if (location.match('comp')) {
        return 'component';
    }

    if (location.match('cat')) {
        return 'category';
    }

    if (location.match('sub')) {
        return 'subdepartament';
    }

    if (location.match('dep')) {
        return 'departament';
    }

    return 'unknown';
}

const ProductListImpression: React.FC<ProductImpressionProps> = ({
    trackedData,
    className,
    children
}) => {
    const ads = useAds();
    const teApi = useContext(TeApiContext);
    const location = teApi?.getPath() ?? '';
    const sitetype = getSitetype(location);
    const disabled = !ads?.adpConfig?.actgid || !ads?.adpConfig?.adpNetwork;

    const onViev = (inView: boolean): void => {
        if (inView) {
            productsImpressions.push(trackedData);

            if (productsImpressions.length > 10) {
                sendData();
            }
        }
    };

    const sendData = (): void => {
        if (productsImpressions.length > 0) {
            const impressions = productsImpressions.splice(0, 10);

            window?.dlApi?.cmd?.push((onetAds): void => onetAds.sendActivityEvent({
                network: ads?.adpConfig?.adpNetwork ?? '',
                actgid: ads?.adpConfig?.actgid ?? '',
                event: 'product_list',
                products: mapToDasImpression(impressions),
                sitetype
            }));
        }
    };

    useEffect((): void | (() => void) => {
        if (disabled === false) {
            const interval = setInterval(() => sendData(), 5000);

            window.addEventListener('beforeunload', sendData, { passive: true });

            return (): void => {
                window.removeEventListener('beforeunload', sendData, true);
                clearInterval(interval);
            };
        }
    }, []);

    if (disabled) {
        return (
            <div className={className || ''}>
                {children}
            </div>
        );
    }

    return (
        <InView
            triggerOnce={true}
            as="div"
            onChange={onViev}
            className={className}
        >
            {children}
        </InView>
    );
};

export default ProductListImpression;
